import React, { useMemo } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import Icon, { IconName } from 'components_new/atoms/Icon'
import Paper from 'components_new/atoms/Paper'
import Text from 'components_new/atoms/Text'

import { filterViewsByPeriod, TDashboardActivityPeriod } from '../utils'
import { TDashboardGroupActivity } from 'types/GlobalDashboardGroup'
import Box from 'components_new/atoms/Box'
import { useTranslation } from 'language/translator'

interface MetricsProps {
  dashboardGroupId: string
  data: TDashboardGroupActivity
  period: TDashboardActivityPeriod
}

const Metrics = (props: MetricsProps) => {
  const { dashboardGroupId, data, period } = props
  const { t, translateEnum } = useTranslation()

  const filteredData = useMemo(() => {
    const { views } = filterViewsByPeriod(data.views, data.created_at, period)

    return {
      numberOfViews: views.length,
      numberOfAccounts: Array.from(new Set(views.map((view) => view.user_id)))
        .length
    }
  }, [period])

  const rankingData = useMemo(() => {
    const dashboardGroups = Object.values(data.dashboard_groups)
      .sort((o1, o2) => o2.total_views - o1.total_views)
      .map((group) => group.id)

    return {
      placement: dashboardGroups.indexOf(dashboardGroupId) + 1,
      total: dashboardGroups.length
    }
  }, [data])

  return (
    <>
      {[
        {
          iconName: 'VisibilityOutlined',
          title: <>{t('global', 'views')}</>,
          subtitle: <>{t('activity_overview', 'views_helper_text')}</>,
          value: filteredData.numberOfViews
        },
        {
          iconName: 'Users',
          title: <>{t('global', 'users')}</>,
          subtitle: <>{t('activity_overview', 'users_helper_text')}</>,
          value: filteredData.numberOfAccounts
        },
        {
          iconName: 'EmojiEventsOutlined',
          title: <>{t('activity_overview', 'ranking')}</>,
          subtitle: <>{t('activity_overview', 'ranking_helper_text')}</>,
          value: rankingData.placement,
          valueAddition: ` ${t('global', 'of', true)} ${rankingData.total}`
        }
      ].map((metric, i) => {
        const isRanking = i === 2

        return (
          <ThemeProvider key={i} theme={getTheme(isRanking ? 'light' : 'dark')}>
            <Paper
              sx={{
                bgcolor: isRanking ? 'brand.background' : undefined,
                p: 2,
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 2,
                  bgcolor: 'background.overlay'
                }}
              >
                <Icon name={metric.iconName as IconName} />
              </Box>
              <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                {isRanking ? (
                  <Icon
                    name={'Lock'}
                    sx={{
                      color: 'text.secondary',
                      fontSize: 12,
                      mr: 0.25,
                      verticalAlign: 'middle'
                    }}
                  />
                ) : null}
                <Text color="text.secondary" variant="caption">
                  {isRanking
                    ? translateEnum('dashboard_activity_periods', 'SINCE_START')
                      .title
                    : translateEnum('dashboard_activity_periods', period).title}
                </Text>
              </Box>
              <Text variant="h3">
                {metric.value}
                {metric.valueAddition ?? null}
              </Text>
              <Text variant="subtitle2">{metric.title}</Text>
              <Text color="text.secondary" variant="caption">
                {metric.subtitle}
              </Text>
            </Paper>
          </ThemeProvider>
        )
      })}
    </>
  )
}

export default Metrics
